import type { SubscriptionTier, SubscriptionTierId } from '../types';

/**
 * Subscription tier configurations for BrickMod
 */
export const SUBSCRIPTION_TIERS: readonly SubscriptionTier[] = [
  {
    id: 'basic',
    name: 'Basic',
    price: 10,
    setLimit: 1,
    features: [
      '1 set at a time',
      'Access to all sets',
      'Free shipping both ways',
      'Pause subscription anytime',
      'Email notifications',
    ],
  },
  {
    id: 'standard',
    name: 'Standard',
    price: 15,
    setLimit: 1,
    features: [
      '1 set at a time',
      'Access to all sets',
      'Free shipping both ways',
      'Pause subscription anytime',
      'Priority waitlist access',
      'Email & push notifications',
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 20,
    setLimit: 1,
    features: [
      '1 set at a time',
      'Access to all sets',
      'Free express shipping',
      'Pause subscription anytime',
      'Top priority waitlist',
      'All notification options',
      'Exclusive early access',
      'Option to add extra slots',
    ],
  },
  {
    id: 'yearly',
    name: 'Yearly All-You-Can-Eat',
    price: 220,
    setLimit: 1,
    features: [
      'All Premium features',
      '11 months for the price of 11',
      'Save 2 months compared to monthly Premium',
      'Locked-in pricing for 12 months',
      'Annual subscriber badge',
    ],
  },
] as const;

/**
 * Get a subscription tier by its ID
 */
export function getTierById(tierId: SubscriptionTierId): SubscriptionTier | undefined {
  return SUBSCRIPTION_TIERS.find((tier) => tier.id === tierId);
}

/**
 * Get the set limit for a given tier ID
 */
export function getTierLimits(tierId: SubscriptionTierId): number {
  const tier = getTierById(tierId);
  return tier?.setLimit ?? 0;
}

/**
 * Get a subscription tier by its display name
 */
export function getTierByName(name: string): SubscriptionTier | undefined {
  return SUBSCRIPTION_TIERS.find(
    (tier) => tier.name.toLowerCase() === name.toLowerCase()
  );
}

/**
 * Get monthly equivalent price (for yearly tier display)
 */
export function getMonthlyEquivalent(tierId: SubscriptionTierId): number {
  const tier = getTierById(tierId);
  if (!tier) return 0;
  if (tierId === 'yearly') {
    return Math.round((tier.price / 12) * 100) / 100;
  }
  return tier.price;
}

/**
 * Calculate savings for yearly vs monthly
 */
export function getYearlySavings(): number {
  const premium = getTierById('premium');
  const yearly = getTierById('yearly');
  if (!premium || !yearly) return 0;
  return premium.price * 12 - yearly.price;
}
